import { Location, formatDate } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  Subject,
  forkJoin,
  of,
} from 'rxjs';
import { Area } from 'src/app/models/area.model';
import {
  ConfigurationGeneration,
  ConfigurationGenerationPropertyType,
  FormatType,
} from 'src/app/models/configurationGeneration.model';
import {
  AccountStatus,
  BuildingType,
  ClientType,
  CustomerAdd,
  CustomerType,
  ServiceType,
  SltStatus,
  TypeOfConnection,
} from 'src/app/models/customer.model';
import { DtCustomerCreate, getInitDtDto } from 'src/app/models/dt.model';
import { Feeder, FeederType } from 'src/app/models/feeder.model';
import { HistoryOutput } from 'src/app/models/history.model';
import {
  BillingType,
  getInitMeter,
  MeterCondition,
  MeterModel,
  MeterStatus,
  MeterType,
} from 'src/app/models/meter.model';
import { Region } from 'src/app/models/region.model';
import { Tariff } from 'src/app/models/tariff.model';
import { User } from 'src/app/models/user.model';
import { OptionObj } from 'src/app/models/util.model';
import { AreaService } from 'src/app/services/area.service';
import { CustomerService } from 'src/app/services/customer.service';
import { DecommissioningService } from 'src/app/services/decommissioning.service';
import { DtService } from 'src/app/services/dt.service';
import { F11Service } from 'src/app/services/f11.service';
import { F33Service } from 'src/app/services/f33.service';
import { MeterModelService } from 'src/app/services/meter-model.service';
import { MeterManagementService } from 'src/app/services/meter-management.service';
import { MeterService } from 'src/app/services/meter.service';
import { RegionService } from 'src/app/services/region.service';
import { TariffService } from 'src/app/services/tariff.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';
import { ValidationService } from 'src/app/services/validation.service';
import { Gender } from 'src/app/_modules/customers/_enums/gender.enum';
import { OwnershipStatus } from 'src/app/_modules/customers/_models/kyc-account-data';
import { NATIONALITIES } from 'src/app/_modules/customers/_models/nationalities.data';
import { IOption } from 'src/app/_modules/shared/_interfaces/option';
import {
  AcLineSegmentService,
  AuthService,
  CimSubstationService,
  GeographicalRegionService,
  PermissionsService,
  SubGeographicalRegionService,
} from 'shared-lib';

@Component({
  selector: 'add-customer',
  templateUrl: './add-customer.component.html',
  styleUrls: ['./add-customer.component.scss'],
})
export class AddCustomerComponent implements OnInit, OnDestroy {
  @ViewChild('openUpdateDescriptionModal')
  openUpdateDescriptionModal: ElementRef;
  history: HistoryOutput = new HistoryOutput();

  isDropdownOpened: boolean = false;
  filteredMobileUsers: User[] = [];
  tempAssignedUserId: number = 0;
  mobileUsers: User[] = [];
  assignedUserId: number = 0;

  readonly cardsCount = 3;
  activeCard = 1;
  saveDisabled: boolean = true;
  isReadOnly: boolean = false;
  successfullyAdded: boolean = false;
  processesFinished: number = 0;

  feederType: FeederType = FeederType.NOT_SELECTED;
  selectedFeederId: number = 0;
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  contractNumber: string =
    this.translationService.getByKeyFromStorage('Contract number');
  utilityId: number = 0;
  sltOptions: OptionObj[] = [];

  filterText: string = '';

  customer: CustomerAdd = {
    id: 0,
    dataQualityId: 0,
    slrn: '',
    name: '',
    accountNumber: '',
    address: '',
    email: undefined,
    phoneNumber: undefined,
    tariffName: '',
    serviceType: null,
    buildingType: null,
    customerType: null,
    typeOfConnection: TypeOfConnection.NotSelected,
    clientType: null,
    regionId: null,
    areaId: null,
    tariffId: null,
    dtId: null,
    isNew: false,
    billingType: BillingType.NotSelected,
    meter: getInitMeter(),
    dt: getInitDtDto(),
    isNewConnection: false,
    history: new HistoryOutput(),
    spn: '',
    blockNumber: null,
    geoCode: null,
    activityCode: '',
    currentPlotCode: '',
    ghanaPostGPS: '',
    sltStatus: undefined,
    area: new Area(),
    buildingId: 0,
    surveyStatus: '',
    utilityId: 0,
    isChecked: false,
    isAudited: false,
    isReviewed: false,
    accountStatus: null,
    installationDate: null,
  };

  accountNumberInvalid: boolean = false;
  accountNumberErrorMessage: string = '';
  spnInvalid: boolean = false;
  spnErrorMessage: string = '';

  originalAccountNumber: string = '';
  originalSpn: string = '';
  originalMeterNumber: string = '';
  originalNin: string = '';

  CustomerType = CustomerType;
  customerTypeOptions = [
    { name: 'Prepaid', value: CustomerType.Prepaid },
    { name: 'Postpaid', value: CustomerType.Postpaid },
  ];

  buildingTypeOptions = [
    { name: 'Residential', value: BuildingType.Residential },
    { name: 'Commercial', value: BuildingType.Commercial },
    { name: 'Industrial', value: BuildingType.Industrial },
    { name: 'Mixed', value: BuildingType.Mixed },
  ];

  billingTypeOptions = [
    { name: 'Prepaid', value: BillingType.Prepaid },
    { name: 'Postpaid', value: BillingType.Postpaid },
  ];

  MeterStatus = MeterStatus;

  meterStatusOptions = [
    { name: 'Unmetered', value: MeterStatus.Unmetered },
    { name: 'Metered', value: MeterStatus.Metered },
  ];

  compatilibity3kctOptions = [
    { name: 'Yes', value: true },
    { name: 'No', value: false },
  ];

  TypeOfConnection = TypeOfConnection;
  customerPhaseOptions = [
    { name: 'SinglePhase', value: TypeOfConnection.SinglePhase },
    { name: 'ThreePhase', value: TypeOfConnection.ThreePhase },
  ];
  ClientType = ClientType;
  clientTypeOptions = [
    { name: 'Company', value: ClientType.Company },
    {
      name: 'EmbassyForeignInstitution',
      value: ClientType.EmbassyForeignInstitution,
    },
    { name: 'Individual', value: ClientType.Individual },
    { name: 'Government', value: ClientType.Government },
    { name: 'Assembly', value: ClientType.Assembly },
  ];

  MeterCondition = MeterCondition;
  meterConditionOptions = [
    { name: 'Working', value: MeterCondition.Working },
    { name: 'NotWorking', value: MeterCondition.NotWorking },
    { name: 'Burnt', value: MeterCondition.Burnt },
  ];
  ServiceType = ServiceType;
  serviceTypeOptions = [
    { name: 'Large', value: ServiceType.Large },
    { name: 'Small', value: ServiceType.Small },
    { name: 'Prepaid', value: ServiceType.Prepaid },
    { name: 'AMR', value: ServiceType.AMR },
  ];
  AccountStatus = AccountStatus;
  accountStatusOptions = [
    { name: 'Active', value: AccountStatus.Active },
    { name: 'Inactive', value: AccountStatus.Inactive },
    { name: 'Pending', value: AccountStatus.Pending },
  ];
  identificationTypeOptions = [
    // Values match Customer.IdType strings saved by the mobile app
    { name: 'COMMON.CUSTOMER.KYC.ID_TYPE_OPTIONS.DRIVERS_LICENSE', value: 'Driver license' },
    { name: 'COMMON.CUSTOMER.KYC.ID_TYPE_OPTIONS.VOTERS_CARD', value: 'Voters card' },
    { name: 'COMMON.CUSTOMER.KYC.ID_TYPE_OPTIONS.INTERNATIONAL_PASSPORT', value: 'International passport' },
  ];
  ownershipStatusOptions = [
    { name: 'COMMON.CUSTOMER.KYC.OWNERSHIP_STATUS_OPTIONS.OWNER', value: OwnershipStatus.Owner },
    { name: 'COMMON.CUSTOMER.KYC.OWNERSHIP_STATUS_OPTIONS.TENANT', value: OwnershipStatus.Tenant },
  ];
  genderOptions = [
    { name: 'COMMON.CUSTOMER.KYC.GENDER_OPTIONS.FEMALE', value: Gender.Female },
    { name: 'COMMON.CUSTOMER.KYC.GENDER_OPTIONS.MALE', value: Gender.Male },
  ];
  // Customer must be at least 18 years old
  maxDateOfBirth: string = formatDate(
    new Date().setFullYear(new Date().getFullYear() - 18), 'yyyy-MM-dd', 'en-US'
  );
  // SltStatus = SltStatus;
  // sltStatusOptions = [
  //   { name: this.translationService.getByKeyFromStorage('NSLT'), value: SltStatus.NSLT },
  //   { name: this.translationService.getByKeyFromStorage('SLT'), value: SltStatus.SLT },
  // ];

  //from back
  regions: Region[] = [];
  areas: Area[] = [];
  selectableAreas: Area[] = [];
  tariffs: Tariff[] = [];
  dts: DtCustomerCreate[] = [];
  meterTypes: MeterType[] = [];
  meterModels: MeterModel[] = [];
  selectableMeterModels: MeterModel[] = [];
  feeders: Feeder[] = [];

  // CIM dropdown data (used when authService.hasCimDataIntegration)
  geoRegions: any[] = [];
  subGeoRegions: any[] = [];
  selectableSubGeoRegions: any[] = [];
  voltageClassOptions = [
    { name: 'LV', value: 1 },
    { name: 'MV', value: 2 },
    { name: 'HV', value: 3 },
  ];
  acLines: any[] = [];
  selectableAcLines: any[] = [];
  substations: any[] = [];
  selectableSubstations: any[] = [];
  selectedGeographicalRegionId: number = 0;
  selectedVoltageClass: number = 0;
  selectedAcLineId: number = 0;
  acLineFilter: string = '';
  substationFilter: string = '';
  filteredAcLines: any[] = [];
  filteredSubstations: any[] = [];
  selectedAcLineName: string = '';
  selectedSubstationName: string = '';
  selectedSubstationNumber: string = '';

  // search dropdowns
  tariffFilter: string = '';
  feederFilter: string = '';
  dtFilter: string = '';
  selectedFeederName: string = '';
  filteredTariffs: Tariff[] = [];
  filteredFeeders: Feeder[] = [];
  filteredDts: DtCustomerCreate[] = [];
  nationalityFilter: string = '';
  filteredNationalities: IOption[] = NATIONALITIES;
  ninValidationStatus: 'pending' | 'valid' | 'invalid' | null = null;
  ninErrorMessage: string = '';
  ninVerificationDisabled: boolean = false;
  ninImageName: string = '';
  private ninRecordNames: { firstName: string; lastName: string } | null = null;
  // NIN the uploaded image was verified for, a changed NIN needs a new image
  private ninVerifiedFor: string = '';

  //select boxes
  //1
  customerType: string = '';
  area: string = '';
  region: string = '';
  buildingType: string = '';
  serviceType: string = '';
  clientType: string = '';
  typeOfConnection: string = '';
  accountStatus: string = '';
  spn: string | null = null;
  blockNumber: string | null = null;
  geoCode: string | null = null;
  activityCode: string = '';
  currentPlotCode: string = '';
  ghanaPostGPS: string = '';
  sltStatus: string = this.translationService.getByKeyFromStorage('SltStatus');

  //2
  tariff?: string = '';
  feeder: string = '';

  //3
  meterType: string = '';
  billingType: string = '';
  meterStatus: string = '';
  meterCondition: string = '';
  compatible3kct?: boolean;

  //4
  accessibilityStatus: string = '';
  surveyCondition: string = '';

  customerId: number = 0;

  selectedDt: DtCustomerCreate = {
    externalId: 0,
    name: '',
    number: '',
    connectedFeederNumber: '',
  };
  isGhanaPostFieldsVisible: boolean = true;
  isGeoCodeFieldVisible: boolean = true;
  isActivityCodeFieldVisible: boolean = true;
  isCurrentPlotFieldVisible: boolean = true;
  isBlockNumberFieldVisible: boolean = true;
  isSpnFieldVisible: boolean = true;
  isServiceTypeFieldVisible: boolean = true;
  dtName: string = this.translationService.getByKeyFromStorage('DTNumber');
  selectedSltStatus: OptionObj = {
    key: 0,
    value: '',
  };
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private location: Location,
    private decommissioningService: DecommissioningService,
    private regionService: RegionService,
    private areaService: AreaService,
    private customerService: CustomerService,
    private tariffService: TariffService,
    private dtService: DtService,
    private meterService: MeterService,
    private meterModelService: MeterModelService,
    private meterManagementService: MeterManagementService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private f11Service: F11Service,
    private f33Service: F33Service,
    private translationService: TranslationService,
    private validationService: ValidationService,
    private userService: UserService,
    public authService: AuthService,
    public permissionsService: PermissionsService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
    private acLineSegmentService: AcLineSegmentService,
    private cimSubstationService: CimSubstationService,
  ) { }

  ngOnInit(): void {
    this.setFieldsVisibility();
    this.isReadOnly = !this.permissionsService.isCustomerCreateEditCrudAllowed;
    let utilityId = parseInt(
      this.route.snapshot.paramMap.get('utilityId') || ''
    );
    this.utilityId = utilityId;
    this.getData();
    this.getMobileUsers();
    this.getAllDts();
  }

  getSltStatus(value?: string) {
    return this.translationService.getByKeyFromStorage(value ?? '');
  }

  compareByKey(option1: OptionObj, option2: OptionObj): boolean {
    return option1 && option2 ? option1.key === option2.key : option1 === option2;
  }

  handleKycAccountData(responseData: any) {
    //card 1
    this.customer = responseData.data;
    this.originalAccountNumber = (this.customer.accountNumber ?? '').trim();
    this.originalSpn = (this.customer.spn ?? '').trim();
    this.originalMeterNumber = (this.customer.meter?.number ?? '').trim();
    this.originalNin = this.customer.nin ?? '';
    this.customer.email =
      this.customer.email == ' ' ? undefined : this.customer.email;
    this.customer.phoneNumber =
      this.customer.phoneNumber == ' ' ? undefined : this.customer.phoneNumber;
    this.customerType =
      this.customer.customerType != null
        ? this.customer.customerType.toString()
        : '';
    this.typeOfConnection =
      this.customer.typeOfConnection != null
        ? this.customer.typeOfConnection.toString()
        : '';

    this.clientType =
      this.customer.clientType != null
        ? ClientType[this.customer.clientType]
        : '';
    this.serviceType =
      this.customer.serviceType != null
        ? this.customer.serviceType.toString()
        : '';
    // this.sltStatus =
    //   this.customer.sltStatus != null ? this.customer.sltStatus.toString() : '';
    if (!this.authService.hasCimDataIntegration) {
      let area = this.areas.find((x) => x.id === this.customer.areaId);
      this.area = area?.name!;
      this.region = this.regions.find(
        (region) => region.id === area?.regionId
      )?.name ?? '';
    } else {
      this.prefillCimFromCustomer();
    }
    this.buildingType = this.customer.buildingType != null
      ? BuildingType[this.customer.buildingType]
      : '';
    this.accountStatus = this.customer.accountStatus != null
      ? this.customer.accountStatus.toString()
      : '';
    this.customer.installationDate = this.toDateInputValue(this.customer.installationDate);
    // API returns enums as names, options bind numeric values
    this.customer.ownershipStatus =
      OwnershipStatus[this.customer.ownershipStatus as unknown as keyof typeof OwnershipStatus];
    this.customer.dateOfBirth = this.toDateInputValue(this.customer.dateOfBirth);

    //card 2
    this.tariff = this.tariffs.find(
      (tariff) => tariff.id == this.customer.tariffId
    )?.name;
    if (!this.authService.hasCimDataIntegration) {
      let dt = this.customer.dt;
    if (dt != null && dt.id != 0) {
        this.feederType = FeederType[this.customer.dt?.feeder?.feederType?.toString() as keyof typeof FeederType];

        this.getFeedersByFeederType(this.feederType);
        this.selectedFeederId = dt.feeder.id;
        this.getDts();
        this.feederType === FeederType.KV11
          ? (this.selectedDt.feeder11 = dt.feeder)
          : (this.selectedDt.feeder33 = dt.feeder);
        this.selectedDt = {
          externalId: dt.id,
          name: dt?.name,
          number: dt?.number,
          connectedFeederNumber: dt?.feeder?.number,
        };
        this.selectedFeederName = dt?.feeder?.name ?? '';
      }
    }

    //card 3
    if (this.customer.meter != null) {
      if (this.customer.meter.meterType != null) {
        this.meterType = this.customer.meter.meterType.name;
      }

      this.filterMeterModels();

      if (this.customer.meter.id) {
        this.customer.meter.status = MeterStatus.Metered;
        this.meterStatus = MeterStatus[MeterStatus.Metered];
      } else {
        this.meterStatus = MeterStatus[MeterStatus.Unmetered];
      }

      this.meterCondition =
        this.customer.meter.condition != null
          ? this.customer.meter.condition.toString()
          : '';

      this.compatible3kct = this.customer.meter.compatibility3KCT;
    } else {
      this.meterStatus = MeterStatus[MeterStatus.Unmetered];
    }
    this.billingType =
      this.customer.billingType != null
        ? this.customer.billingType.toString()
        : '';
  }

  private prefillCimFromCustomer() {
    if (this.customer.subgeographicalRegionId) {
      const sgr = this.subGeoRegions.find(
        (x) => x.id === this.customer.subgeographicalRegionId,
      );
      const parentId = sgr?.geographicalRegion?.id ?? 0;
      if (parentId) {
        this.selectedGeographicalRegionId = parentId;
        this.customer.geographicalRegionId = parentId;
        this.selectableSubGeoRegions = this.subGeoRegions.filter(
          (x) => x.geographicalRegion?.id === parentId,
        );
      }
    }

    if (!this.customer.substationId) return;

    this.selectedSubstationName = this.customer.dt?.name ?? '';
    this.selectedSubstationNumber = this.customer.dt?.number ?? '';
    this.selectedAcLineName = this.customer.dt?.feeder?.name ?? '';
    this.selectedAcLineId = this.customer.acLineId ?? 0;

    const voltageClass = this.customer.dt?.feeder?.voltageClass;
    if (voltageClass) {
      this.selectedVoltageClass = voltageClass;
      this.customer.voltageClass = voltageClass;
      this.getACLinesByVoltageClass(this.selectedVoltageClass);
      this.getSubstations();
    }
  }

  getCustomer() {
    let id = this.route.snapshot.paramMap.get('id') || '';
    if (id) {
      this.customerId = parseInt(id);
      this.customerService.getCrud(this.customerId).subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.handleKycAccountData(responseData);
            this.selectedSltStatus.key = this.customer.sltStatus?.id ?? 0;
            this.selectedSltStatus.value = this.customer.sltStatus?.value ?? '';
            this.filterTariffOptions();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
    }
  }

  makeOldMeterAvailable() {
    if (!this.customer.oldMeterId) {
      this.toastr.info(
        this.translateService.instant(
          'COMMON.OLD_METER_AVAILABILITY.UPLOAD_ON_INVENTORY'
        )
      );
      return;
    }
    this.meterManagementService
      .makeMeterAvailable(this.customer.oldMeterId)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.customer.oldMeterIsMadeAvailable = true;
            if (responseData.data) {
              this.toastr.success(
                this.translateService.instant(
                  'COMMON.OLD_METER_AVAILABILITY.AVAILABLE_FOR_INSTALLATION'
                )
              );
            } else {
              this.toastr.info(
                this.translateService.instant(
                  'COMMON.OLD_METER_AVAILABILITY.UPLOAD_ON_INVENTORY'
                )
              );
            }
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => this.toastr.error('Error occured'),
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  continue(): void {
    if (this.isReadOnly) {
      this.activeCard++;
      return;
    }
    if (this.activeCard === 1 && this.customer.tariffId == null) {
      this.toastr.error('Tariff is a mandatory field.');
      return;
    }
    if (this.activeCard === 1 && this.ninInvalid) {
      this.toastr.error(this.translateService.instant('COMMON.CUSTOMER.KYC.NIN_INVALID'));
      return;
    }
    if (this.activeCard === 1 && this.ninVerificationError) {
      this.toastr.error(this.translateService.instant(this.ninVerificationError));
      return;
    }
    if (!this.authService.hasCimDataIntegration && this.activeCard === 2 && this.customer.dtId != null && this.dts.length > 0) {
      const chosenDt = this.dts.find((x) => x.name === this.selectedDt.name);
      if (chosenDt === undefined) {
        this.toastr.error('Please choose valid DT name.');
        return;
      }
    }
    if (this.activeCard === 2 &&
      (this.authService.hasCimDataIntegration ? this.customer.substationId == null : this.customer.dtId == null)) {
      this.toastr.error('DT is a mandatory field.');
      return;
    }
    if (!this.validationService.validateValue(this.customer.accountNumber ?? '', 'AccountNumber')) {
      this.toastr.error('Invalid Account number')
      return;
    }
    const accountNumberConfig = this.getConfig(ConfigurationGenerationPropertyType.AccountNumber);
    if (accountNumberConfig && !this.matchesConfig(this.customer.accountNumber ?? '', accountNumberConfig)) {
      this.accountNumberInvalid = true;
      this.accountNumberErrorMessage = this.describeConfig(accountNumberConfig);
      this.toastr.error(`Invalid Account number. ${this.accountNumberErrorMessage}`);
      return;
    }
    const spnConfig = this.getConfig(ConfigurationGenerationPropertyType.Spn);
    if (this.isSpnFieldVisible && spnConfig && !this.matchesConfig(this.customer.spn ?? '', spnConfig)) {
      this.spnInvalid = true;
      this.spnErrorMessage = this.describeConfig(spnConfig);
      this.toastr.error(`Invalid ${this.contractNumber}. ${this.spnErrorMessage}`);
      return;
    }
    if (this.activeCard === 3 && this.meterStatus === 'Metered' && !this.validationService.validateValue(this.customer.meter.number ?? '', 'MeterNumber')) {
      this.toastr.error('Invalid Meter number')
      return;
    }

    this.activeCard++;
    if (this.activeCard > this.cardsCount) {
      this.saveDisabled = false;
    }
  }

  setActiveCard(activeCard: number): void {
    this.activeCard = activeCard;
    this.saveDisabled = true;
  }

  cancelClicked() {
    this.location.back();
  }

  save() {
    let isNewConnection =
      this.route.snapshot.paramMap.get('isNewConnection') || '';
    this.customer.utilityId = this.utilityId;
    if (isNewConnection) {
      this.customer.isNewConnection = isNewConnection == 'true';
    }
    if (this.selectedSltStatus.key && this.selectedSltStatus.value) {
      this.customer.sltStatus = new SltStatus();
      this.customer.sltStatus.id = this.selectedSltStatus.key;
      this.customer.sltStatus.value = this.selectedSltStatus.value;
    }
    this.customer.installationDate = this.customer.installationDate || null;
    this.customer.dateOfBirth = this.customer.dateOfBirth || null;
    this.customerService.addCustomer(this.customer).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === "OK") {
          if (this.customer.id != 0) //close if edit
          {
            this.openUpdateDescriptionModal?.nativeElement?.click();
          }
          this.successfullyAdded = true;
          setTimeout(() => {
            this.successfullyAdded = false;
            this.router.navigate(['/crud/9']);
          }, 2000);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  finishProcess() {
    this.processesFinished++;
    if (this.processesFinished == 6) {
      this.processesFinished = 0;
      this.getCustomer();
    }
  }

  getData() {
    if (this.authService.hasCimDataIntegration) {
      this.getGeoRegions();
      this.getSubGeoRegions();
    } else {
      this.getRegions();
      this.getAreas();
    }
    this.getTariffs();
    this.getMeterTypes();
    this.getMeterModels();
    this.getSltStatuses();
  }

  getGeoRegions() {
    this.geographicalRegionService.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geoRegions = responseData.data ?? [];
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: () => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  getSubGeoRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.subGeoRegions = responseData.data ?? [];
          this.selectableSubGeoRegions = this.subGeoRegions;
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: () => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  getACLinesByVoltageClass(voltageClass: number) {
    this.acLineSegmentService
      .getAllACLinesCached({ voltageClasses: [voltageClass] })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.acLines = responseData.data ?? [];
            this.filteredAcLines = this.acLines;
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: () => this.toastr.error('Error occured'),
      });
  }

  getSubstations() {
    this.cimSubstationService
      .getAllSubstationsCached({ voltageClasses: [] })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.substations = responseData.data ?? [];
            this.refreshSelectableSubstations();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: () => this.toastr.error('Error occured'),
      });
  }

  selectGeographicalRegion(e: any) {
    this.selectedGeographicalRegionId = +e.target.value;
    this.customer.geographicalRegionId = this.selectedGeographicalRegionId || null;
    this.selectableSubGeoRegions = this.subGeoRegions.filter(
      (sgr) => sgr.geographicalRegion?.id === this.selectedGeographicalRegionId,
    );
    this.customer.subgeographicalRegionId = null;
  }

  selectSubGeographicalRegion(e: any) {
    this.customer.subgeographicalRegionId = e.target.value !== '' ? +e.target.value : null;
    const sgr = this.subGeoRegions.find((x) => x.id === this.customer.subgeographicalRegionId);
    const parentId = sgr?.geographicalRegion?.id ?? 0;
    if (parentId) {
      this.selectedGeographicalRegionId = parentId;
      this.customer.geographicalRegionId = parentId;
      this.selectableSubGeoRegions = this.subGeoRegions.filter(
        (x) => x.geographicalRegion?.id === parentId,
      );
    }
  }

  selectVoltageClass(e: any) {
    this.selectedVoltageClass = +e.target.value;
    this.customer.voltageClass = this.selectedVoltageClass || null;
    this.selectedAcLineId = 0;
    this.selectedAcLineName = '';
    this.acLineFilter = '';
    this.customer.acLineId = null;
    this.customer.substationId = null;
    this.selectedSubstationName = '';
    this.selectedSubstationNumber = '';
    this.substationFilter = '';
    this.substations = [];
    this.filteredSubstations = [];
    if (!this.selectedVoltageClass) {
      this.acLines = [];
      this.filteredAcLines = [];
      return;
    }
    this.getACLinesByVoltageClass(this.selectedVoltageClass);
    this.getSubstations();
  }

  filterAcLineOptions() {
    this.filteredAcLines = this.acLines.filter((a) =>
      (a.name ?? '').toLowerCase().includes(this.acLineFilter.toLowerCase()),
    );
  }

  selectAcLineOption(line: any) {
    this.selectedAcLineId = line.id;
    this.selectedAcLineName = line.name;
    this.customer.acLineId = line.id;
    this.acLineFilter = '';
    this.filteredAcLines = this.acLines;
    this.customer.substationId = null;
    this.selectedSubstationName = '';
    this.selectedSubstationNumber = '';
    this.refreshSelectableSubstations();
  }

  filterSubstationOptions() {
    this.filteredSubstations = this.selectableSubstations.filter((s) =>
      (s.name ?? '').toLowerCase().includes(this.substationFilter.toLowerCase()),
    );
  }

  selectSubstationOption(substation: any) {
    this.customer.substationId = substation.id;
    this.selectedSubstationName = substation.name;
    this.selectedSubstationNumber = substation.number ?? '';
    this.substationFilter = '';
    this.filteredSubstations = this.selectableSubstations;
    if (!this.selectedAcLineId) {
      this.backfillAcLineFromSubstation(substation);
    }
  }

  private backfillAcLineFromSubstation(substation: any) {
    const acLineId = (substation.incomingACLines ?? [])
      .map((l: any) => l.id)
      .find((id: number) => this.acLines.some((a) => a.id === id));
    if (!acLineId) {
      return;
    }
    const acLine = this.acLines.find((a) => a.id === acLineId);
    this.selectedAcLineId = acLineId;
    this.selectedAcLineName = acLine?.name ?? '';
    this.customer.acLineId = acLineId;
    this.acLineFilter = '';
    this.filteredAcLines = this.acLines;
  }

  private refreshSelectableSubstations() {
    if (this.selectedAcLineId) {
      this.selectableSubstations = this.substations.filter((s) =>
        (s.incomingACLines ?? []).some((l: any) => l.id === this.selectedAcLineId),
      );
    } else {
      this.selectableSubstations = this.substations;
    }
    this.filteredSubstations = this.selectableSubstations;
  }

  getRegions() {
    this.regionService.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: (_) => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  getAreas() {
    this.areaService.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.areas = responseData.data;
          this.selectableAreas = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }

        this.finishProcess();
      },
      error: (_) => {
        this.finishProcess();
        this.toastr.error('Error occured');
      },
    });
  }

  getTariffs() {
    this.tariffService.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.tariffs = responseData.data;
          this.filteredTariffs = this.getTariffsMatchingCriteria();
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: (_) => {
        this.finishProcess();
        this.toastr.error('Error occured');
      },
    });
  }

  getAllDts() {
    this.dtService
      .getAllForSelect()
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK' || response.status == 204) {
            this.dts = responseData.data;
            this.filteredDts = this.dts;
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => this.toastr.error('Error occured'),
      });
  }
  getDts() {
    this.dtService
      .getAllByFeeder(
        this.selectedFeederId,
        this.feederType.toString(),
        this.utilityId
      )
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK' || response.status == 204) {
            this.dts = responseData.data;
            this.filteredDts = this.dts;
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => this.toastr.error('Error occured'),
      });
  }

  getDtByName(name: string) {
    this.dtService.getAllByName(name).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          const dtsFromResponse = responseData.data;

          this.dts = dtsFromResponse.map((obj: any) => ({
            name: obj.value,
            externalId: Number(obj.key),
          })) as DtCustomerCreate[];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  getMeterTypes() {
    this.meterService.getAllMeterTypesForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.meterTypes = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: (_) => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  getMeterModels() {
    this.meterModelService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.meterModels = responseData.data ?? [];
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: (_) => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  selectCustomerType(e: any) {
    this.customer.customerType = +e.target.value;
    this.customerType = CustomerType[e.target.value];
    this.selectBillingType(e);
    this.filterTariffsByCriteria();
  }

  selectBuildingType(e: any) {
    if (e.target.value == '') this.customer.buildingType = null;
    else this.customer.buildingType = +e.target.value;
    this.buildingType = BuildingType[e.target.value];
  }
  selectCustomerPhase(e: any) {
    if (e.target.value == '')
      this.customer.typeOfConnection = TypeOfConnection.NotSelected;
    else this.customer.typeOfConnection = +e.target.value;

    this.typeOfConnection = TypeOfConnection[e.target.value];
  }
  selectClientType(e: any) {
    if (e.target.value == '') this.customer.clientType = null;
    else this.customer.clientType = +e.target.value;
    this.clientType = ClientType[e.target.value];
  }
  selectServiceType(e: any) {
    if (e.target.value == '') this.customer.serviceType = null;
    else this.customer.serviceType = +e.target.value;
    this.serviceType = ServiceType[e.target.value];
    this.filterTariffsByCriteria();
  }

  selectAccountStatus(e: any) {
    if (e.target.value == '') this.customer.accountStatus = null;
    else this.customer.accountStatus = +e.target.value as AccountStatus;
    this.accountStatus = AccountStatus[e.target.value];
  }

  // selectSltStatus(e: any) {
  //   if (e.target.value != '') {
  //     this.customer.sltStatus = parseInt(e.target.value);
  //   } else {
  //     this.customer.sltStatus = undefined;
  //   }
  // }

  selectRegion(e: any) {
    this.customer.regionId = +e.target.value;
    this.selectableAreas = this.areas.filter(
      (area) => area.regionId === this.customer.regionId
    );
    if (this.selectableAreas.length === 0) {
      this.area = '';
    }
    this.region = this.regions
      .filter((x) => x.id == this.customer.regionId)
      .toString();

    this.customer.areaId = null;
  }

  selectArea(e: any) {
    this.customer.areaId = +e.target.value;
    const area = this.areas.find((x) => x.id === this.customer.areaId);
    this.area = area?.name!;
    const region = this.regions.find((region) => region.id === area?.regionId)!;
    this.customer.regionId = region.id;
    this.region = region.name;
  }

  selectTariff(e: any) {
    this.customer.tariffId = e.target.value !== '' ? +e.target.value : null;
    this.tariff = this.customer.tariffId != null ? this.getTariffName(this.customer.tariffId)! : '';
  }

  getTariffName(id: number) {
    return this.tariffs.find((x) => x.id === id)?.name;
  }

  // setDtName(e: any) {
  //   this.chosenDt = false;
  //   this.dtNameUpdateSubject.next(e.target.value);
  // }

  selectMeterType(e: any) {
    this.customer.meter.typeId = +e.target.value;
    this.meterType = this.getMeterTypeName(this.customer.meter.typeId)!;
    if (this.customer.meter.meterType == null) {
      this.customer.meter.meterType = new MeterType();
    }
    this.customer.meter.meterType.name = this.meterType;
    this.customer.meter.meterType.id = parseInt(e.target.value);

    this.filterMeterModels();
  }

  filterMeterModels() {
    this.selectableMeterModels = this.meterModels.filter(
      (x) => x.meterBrandId === this.customer.meter.typeId
    );
    if (
      !this.selectableMeterModels.some(
        (x) => x.id === this.customer.meter.meterModelId
      )
    ) {
      this.customer.meter.meterModelId = undefined;
    }
  }

  getMeterTypeName(id: number) {
    return this.meterTypes.find((x) => x.id === id)?.name;
  }

  selectMeterModel(e: any) {
    this.customer.meter.meterModelId = e.target.value
      ? +e.target.value
      : undefined;
  }

  getMeterModelName(id?: number) {
    return this.meterModels.find((x) => x.id === id)?.name;
  }

  selectMeterStatus(e: any) {
    var selectedStatus = +e.target.value as MeterStatus;
    if (selectedStatus == MeterStatus.Unmetered) {
      this.customer.meter = getInitMeter();
    }
    this.customer.meter.status = selectedStatus;
    this.meterStatus = MeterStatus[this.customer.meter.status];
  }

  select3kctCompatibility(e: any) {
    this.customer.meter.compatibility3KCT = e.target.value == 'true';
  }

  selectMeterCondition(e: any) {
    this.customer.meter.condition = +e.target.value;
    this.meterCondition = MeterCondition[this.customer.meter.condition];
  }

  selectBillingType(e: any) {
    this.customer.billingType = +e.target.value;
    this.billingType = BillingType[this.customer.billingType];
  }

  // subscribeToDtNameChange() {
  //   this.dtNameUpdate$.pipe(
  //       debounceTime(500),
  //       distinctUntilChanged(),
  //       takeUntil(this.ngUnsubscribe)
  //   ).subscribe(value => {
  //     this.dtName = value;
  //     if(value.length < 3){
  //       if(this.selectedDt.number != ''){
  //         this.selectedDt.number = '';
  //         this.selectedDt.connectedFeederNumber = '';
  //       }
  //       return;
  //     }
  //     this.getDts(value);
  //     const dt = this.dts.find(dt => dt.name.includes(value))!;
  //     if(dt === undefined) {
  //       this.selectedDt = {externalId: 0, name: '', number: '', connectedFeederNumber: ''};
  //       this.customer.dtId = 0;
  //       return;
  //     };
  //     this.selectedDt = {externalId: dt.externalId, name: dt?.name, number: dt?.number, connectedFeederNumber: dt?.connectedFeederNumber}
  //     this.customer.dtId = dt.externalId;
  //   })
  // }

  selectDt(e: any) {
    this.selectedDt = this.dts.find((x) => x.externalId === +e.target.value)!;
    this.customer.dtId = e.target.value !== '' ? +e.target.value : null;
  }

  updateHistory(history: any) {
    this.history = history;
    this.customer.history = this.history;
    this.save();
  }

  saveOrUpdate() {
    if (this.isReadOnly) {
      return;
    }
    this.validateDuplicatesThen(() => {
      if (this.customer.id != 0) {
        this.openUpdateDescriptionModal?.nativeElement?.click(); //open if edit
        return;
      }
      this.save();
    });
  }

  private validateDuplicatesThen(proceed: () => void) {
    const accountNumber = (this.customer.accountNumber ?? '').trim();
    const spn = (this.customer.spn ?? '').trim();
    const meterNumber = (this.customer.meter?.number ?? '').trim();

    const checkAccount = accountNumber && accountNumber !== this.originalAccountNumber;
    const checkSpn = spn && spn !== this.originalSpn;
    const checkMeter = meterNumber && meterNumber !== this.originalMeterNumber;

    if (!checkAccount && !checkSpn && !checkMeter) {
      proceed();
      return;
    }

    forkJoin({
      account: checkAccount
        ? this.customerService.getCustomerByAccNo(accountNumber)
        : of(null),
      spn: checkSpn ? this.customerService.checkSpnExists(spn) : of(null),
      meter: checkMeter
        ? this.customerService.checkMeterNumberExists(meterNumber)
        : of(null),
    }).subscribe({
      next: (res: any) => {
        if (checkAccount && res.account?.body?.data) {
          this.toastr.error('Customer with Account Number already exists.');
          return;
        }
        if (checkSpn && res.spn?.body?.data === true) {
          this.toastr.error('Customer with SPN already exists.');
          return;
        }
        if (checkMeter && res.meter?.body?.data === true) {
          this.toastr.error('Customer with Meter Number already exists.');
          return;
        }
        proceed();
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectFeederType(e: any) {
    this.feederType = FeederType[e.target.value.toString() as keyof typeof FeederType];
    this.getFeedersByFeederType(+this.feederType);
    this.selectedFeederId = 0;
    this.selectedFeederName = '';
    this.feederFilter = '';
    this.dtFilter = '';
    this.filteredDts = [];
    this.customer.dtId = null;
    this.selectedDt = {
      externalId: 0,
      name: '',
      number: '',
      connectedFeederNumber: '',
    };
  }

  getFeedersByFeederType(type: FeederType, onLoaded?: () => void) {
    if (type === FeederType.KV11) {
      this.getFeeders11(onLoaded);
      return;
    }
    this.getFeeders33(onLoaded);
  }

  getFeeders11(onLoaded?: () => void) {
    this.f11Service.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          const feedersFromResponse = responseData.data;
          this.feeders = feedersFromResponse.map((el: Feeder) => ({
            ...el,
            feederType: FeederType.KV11,
          }));
          this.filteredFeeders = this.feeders;
          onLoaded?.();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getFeeders33(onLoaded?: () => void) {
    this.f33Service.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          const feedersFromResponse = responseData.data;
          this.feeders = feedersFromResponse.map((el: Feeder) => ({
            ...el,
            feederType: FeederType.KV33,
          }));
          this.filteredFeeders = this.feeders;
          onLoaded?.();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectFeeder(e: any) {
    this.selectedFeederId = +e.target.value;
    if (this.feederType === FeederType.KV11) {
      this.selectedDt.feeder11 = this.feeders.find(
        (x) => x.id === this.selectedFeederId
      )!;
      this.selectedDt.connectedFeederNumber = this.selectedDt.feeder11.number;
      this.selectedDt.externalId = 0;
      this.selectedDt.number = '';
    } else {
      this.selectedDt.feeder33 = this.feeders.find(
        (x) => x.id === this.selectedFeederId
      )!;
      this.selectedDt.connectedFeederNumber = this.selectedDt.feeder33.number;
      this.selectedDt.externalId = 0;
      this.selectedDt.number = '';
    }

    this.getDts();
  }

  filterTariffOptions() {
    this.filteredTariffs = this.getTariffsMatchingCriteria().filter(t =>
      t.name.toLowerCase().includes(this.tariffFilter.toLowerCase())
    );
  }

  get ninInvalid(): boolean {
    return !!this.customer.nin && !/^\d{11}$/.test(this.customer.nin);
  }

  // A saved NIN was already verified, only a new or changed one needs an image
  get ninChanged(): boolean {
    return !!this.customer.nin && this.customer.nin !== this.originalNin;
  }

  // Translation key or API message, empty when the NIN can be saved
  get ninVerificationError(): string {
    if (!this.ninChanged || this.ninVerificationDisabled) return '';
    if (this.ninVerifiedFor !== this.customer.nin) return 'COMMON.CUSTOMER.KYC.NIN_IMAGE_REQUIRED';
    if (this.ninValidationStatus === 'pending') return 'COMMON.CUSTOMER.KYC.NIN_VERIFICATION_PENDING';
    if (this.ninValidationStatus === 'invalid') return this.ninErrorMessage;
    if (!this.ninNameMatches()) return 'COMMON.CUSTOMER.KYC.NIN_NAME_MISMATCH';
    return '';
  }

  get ninImageState(): 'empty' | 'pending' | 'valid' | 'invalid' | 'skipped' {
    if (this.ninVerifiedFor !== this.customer.nin) return 'empty';
    if (this.ninVerificationDisabled) return 'skipped';
    if (this.ninValidationStatus === 'pending') return 'pending';
    return this.ninVerificationError ? 'invalid' : 'valid';
  }

  ninImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared so picking the same file again verifies again
    input.value = '';
    if (!file) return;

    this.ninImageName = file.name;
    const image = new Image();
    image.onload = () => {
      // Scaled down to the size of the KYC selfie to keep the request small
      const scale = Math.min(1, 640 / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = image.width * scale;
      canvas.height = image.height * scale;
      canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(image.src);
      this.verifyNin(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
    };
    image.src = URL.createObjectURL(file);
  }

  private verifyNin(selfieImage: string) {
    this.ninVerifiedFor = this.customer.nin!;
    this.ninValidationStatus = 'pending';
    this.ninErrorMessage = '';
    this.ninRecordNames = null;

    this.customerService.validateNin({ nin: this.ninVerifiedFor, selfieImage, id: this.customer.id }).subscribe({
      next: (response) => {
        const responseBody = response.body!;
        if (responseBody.status == 'OK') {
          this.ninValidationStatus = 'valid';
          this.ninRecordNames = responseBody.data;
          if (this.ninNameMatches()) {
            this.toastr.success(responseBody.message);
          } else {
            this.toastr.error(this.translateService.instant('COMMON.CUSTOMER.KYC.NIN_NAME_MISMATCH'));
          }
        } else if (responseBody.status == 'NotImplemented') {
          this.toastr.warning(this.translateService.instant('COMMON.CUSTOMER.KYC.NIN_VERIFICATION_DISABLED'));
          this.ninVerificationDisabled = true;
          this.ninValidationStatus = null;
        } else {
          this.ninValidationStatus = 'invalid';
          this.ninErrorMessage = responseBody.message || 'COMMON.CUSTOMER.KYC.NIN_VERIFICATION_FAILED';
          this.toastr.error(this.translateService.instant(this.ninErrorMessage));
        }
      },
      error: () => {
        this.ninValidationStatus = 'invalid';
        this.ninErrorMessage = 'COMMON.CUSTOMER.KYC.NIN_VERIFICATION_FAILED';
        this.toastr.error(this.translateService.instant(this.ninErrorMessage));
      },
    });
  }

  // Same rule as the KYC account: the NIN first name or surname must be part of the customer name
  private ninNameMatches(): boolean {
    const nameParts = (this.customer.customerName ?? '').toLowerCase().trim().split(/\s+/);
    const ninFirstName = this.ninRecordNames?.firstName?.toLowerCase().trim();
    const ninLastName = this.ninRecordNames?.lastName?.toLowerCase().trim();
    return (!!ninFirstName && nameParts.includes(ninFirstName)) || (!!ninLastName && nameParts.includes(ninLastName));
  }

  filterNationalityOptions() {
    this.filteredNationalities = NATIONALITIES.filter(n =>
      n.displayValue.toLowerCase().includes(this.nationalityFilter.toLowerCase())
    );
  }

  selectNationalityOption(n: IOption) {
    this.customer.nationality = n.displayValue;
    this.nationalityFilter = '';
    this.filteredNationalities = NATIONALITIES;
  }

  selectTariffOption(t: Tariff) {
    this.customer.tariffId = t.id;
    this.tariff = t.name;
    this.tariffFilter = '';
    this.filteredTariffs = this.getTariffsMatchingCriteria();
  }

  private tariffMatchesCriteria(t: Tariff): boolean {
    // The API returns customerTypes/serviceTypes as enum name strings
    // (e.g. "Prepaid", "Large"), so compare against the enum name.
    const customerTypeOk =
      !this.customer.customerType ||
      !t.customerTypes?.length ||
      t.customerTypes.some(
        (ct) => ct.toString() === this.customerType
      );
   
    const serviceTypeOk =
      !this.customer.serviceType ||
      !t.serviceTypes?.length ||
      t.serviceTypes.some(
        (st) => st.toString() === this.serviceType
      );
    const sltStatusOk =
      !this.selectedSltStatus.key ||
      !t.sltStatusId ||
      t.sltStatusId === this.selectedSltStatus.key;
    return customerTypeOk && serviceTypeOk && sltStatusOk;
  }

  private getTariffsMatchingCriteria(): Tariff[] {
    return this.tariffs.filter(t => this.tariffMatchesCriteria(t));
  }

  filterTariffsByCriteria() {
    this.tariffFilter = '';
    const matchingTariffs = this.getTariffsMatchingCriteria();
    this.filteredTariffs = matchingTariffs;
    if (
      this.customer.tariffId != null &&
      !matchingTariffs.some(t => t.id === this.customer.tariffId)
    ) {
      this.customer.tariffId = null;
      this.tariff = '';
    }
  }

  filterFeederOptions() {
    this.filteredFeeders = this.feeders.filter(f =>
      f.name.toLowerCase().includes(this.feederFilter.toLowerCase())
    );
  }

  selectFeederOption(feeder: Feeder) {
    this.selectedFeederId = feeder.id;
    this.selectedFeederName = feeder.name;
    this.feederFilter = '';
    this.filteredFeeders = this.feeders;
    this.customer.dtId = null;
    this.selectedDt = { externalId: 0, name: '', number: '', connectedFeederNumber: '' };
    if (this.feederType === FeederType.KV11) {
      this.selectedDt.feeder11 = feeder;
      this.selectedDt.connectedFeederNumber = feeder.number;
    } else {
      this.selectedDt.feeder33 = feeder;
      this.selectedDt.connectedFeederNumber = feeder.number;
    }
    this.getDts();
  }

  filterDtOptions() {
    this.filteredDts = this.dts.filter(dt =>
      dt.name.toLowerCase().includes(this.dtFilter.toLowerCase())
    );
  }

  selectDtOption(dt: DtCustomerCreate) {
    this.selectedDt = dt;
    this.customer.dtId = dt.externalId;
    this.dtFilter = '';
    this.filteredDts = this.dts;
    this.backfillFeederFromDt(dt);
  }

  backfillFeederFromDt(dt: DtCustomerCreate) {
    const feederId = dt.feeder11Id ?? dt.feeder33Id;
    if (!feederId) {
      return;
    }
    this.feederType = dt.feeder11Id ? FeederType.KV11 : FeederType.KV33;
    this.selectedFeederId = feederId;
    this.feederFilter = '';
    this.getFeedersByFeederType(this.feederType, () => {
      const feeder = this.feeders.find((x) => x.id === feederId);
      if (!feeder) {
        return;
      }
      this.selectedFeederName = feeder.name;
      this.selectedDt.connectedFeederNumber = feeder.number;
      if (this.feederType === FeederType.KV11) {
        this.selectedDt.feeder11 = feeder;
      } else {
        this.selectedDt.feeder33 = feeder;
      }
    });
  }

  setFieldsVisibility() {
    let notVisibleFields: any = localStorage.getItem('notVisibleFields');

    if (!notVisibleFields) {
      return;
    }

    notVisibleFields = JSON.parse(notVisibleFields);
    this.isGhanaPostFieldsVisible = notVisibleFields.find(
      (el: any) => el === 'ghanaPostCode'
    )
      ? false
      : true;
    this.isCurrentPlotFieldVisible = notVisibleFields.find(
      (el: any) => el === 'currentPlotCode'
    )
      ? false
      : true;
    this.isActivityCodeFieldVisible = notVisibleFields.find(
      (el: any) => el === 'activityCode'
    )
      ? false
      : true;
    this.isGeoCodeFieldVisible = notVisibleFields.find(
      (el: any) => el === 'geoCode'
    )
      ? false
      : true;
    this.isSpnFieldVisible = notVisibleFields.find((el: any) => el === 'spn')
      ? false
      : true;
    this.isServiceTypeFieldVisible = notVisibleFields.find(
      (el: any) => el === 'serviceType'
    )
      ? false
      : true;
    this.isBlockNumberFieldVisible = notVisibleFields.find(
      (el: any) => el === 'blockNumber'
    )
      ? false
      : true;
  }

  getSltStatuses() {
    this.customerService.getSltStatusesForOptions().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data;
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
        this.finishProcess();
      },
      error: (_) => {
        this.toastr.error('Error occured');
        this.finishProcess();
      },
    });
  }

  filterMobileUsers() {
    this.isDropdownOpened = true;
    this.tempAssignedUserId = 0;
    this.getMobileUsers();
  }

  getMobileUsers() {
    const areaId = this.authService.hasCimDataIntegration
      ? (this.customer.subgeographicalRegionId ?? 0)
      : (this.customer.areaId ?? 0);

    this.userService
      .getMobileUsers(areaId, this.filterText)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.mobileUsers = responseData.data;
            if (this.mobileUsers.length == 0) {
              this.isDropdownOpened = false;
            } else {
              this.isDropdownOpened = true;
            }
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  selectFieldAgent(id: number) {
    this.isDropdownOpened = false;
    this.assignedUserId = id;
    this.tempAssignedUserId = id;
    var mobUser = this.mobileUsers.find((x) => x.id == id);
    this.filterText = mobUser!.fullName;
  }

  assignForDecommission(): void {
    if (this.isReadOnly) {
      return;
    }
    if (this.mobileUsers.filter((x) => x.fullName == this.filterText.trim()).length > 0) {
      this.assignedUserId = this.mobileUsers.filter((x) => x.fullName == this.filterText.trim())[0].id;
      this.tempAssignedUserId = this.assignedUserId;
    } else {
      this.tempAssignedUserId = 0;
    }
    if (this.tempAssignedUserId != 0) {
      this.decommissioningService.assignForDecommissioning(this.customerId, this.tempAssignedUserId).subscribe({
        next: (response) => {
          if (response.body?.status === 200 || response.body?.status === "OK")
            this.toastr.success(response.body.data);
          else
            this.toastr.error(response.body?.message);
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
    } else {
      this.toastr.warning('Please select valid user.');
    }
  }

  onAccountNumberBlur(): void {
    const value = (this.customer.accountNumber ?? '').trim();
    const config = this.getConfig(ConfigurationGenerationPropertyType.AccountNumber);
    if (!value || !config) {
      this.accountNumberInvalid = false;
      return;
    }
    this.accountNumberInvalid = !this.matchesConfig(value, config);
    this.accountNumberErrorMessage = this.accountNumberInvalid ? this.describeConfig(config) : '';
  }

  onSpnBlur(): void {
    const value = (this.customer.spn ?? '').trim();
    const config = this.getConfig(ConfigurationGenerationPropertyType.Spn);
    if (!value || !config) {
      this.spnInvalid = false;
      return;
    }
    this.spnInvalid = !this.matchesConfig(value, config);
    this.spnErrorMessage = this.spnInvalid ? this.describeConfig(config) : '';
  }

  private describeConfig(config: ConfigurationGeneration): string {
    const prefix = config.startsWith ?? '';
    return prefix
      ? `Must start with "${prefix}" followed by digits only, ${config.length} characters total.`
      : `Must contain digits only, ${config.length} characters total.`;
  }

  private getConfig(propertyType: ConfigurationGenerationPropertyType): ConfigurationGeneration | null {
    const raw = localStorage.getItem('accountNumberAndSpnRules');
    if (!raw) {
      return null;
    }
    const rules: ConfigurationGeneration[] = JSON.parse(raw).map((r: any) =>
      ConfigurationGeneration.fromData(r)
    );
    return rules.find((r) => r.propertyType === propertyType) ?? null;
  }

  private matchesConfig(value: string, config: ConfigurationGeneration): boolean {
    if (!value) {
      return false;
    }
    if (value.length != config.length!) {
      return false;
    }
    const prefix = config.startsWith ?? '';
    if (value.length < prefix.length) {
      return false;
    }
    const actualPrefix = value.substring(0, prefix.length);
    if (!this.prefixMatchesFormat(actualPrefix, prefix, config.formatType)) {
      return false;
    }
    const remainder = value.substring(prefix.length);
    return /^[0-9]*$/.test(remainder);
  }

  private prefixMatchesFormat(actualPrefix: string, configuredPrefix: string, formatType: FormatType | null): boolean {
    switch (formatType) {
      case FormatType.Numeric:
        return actualPrefix === configuredPrefix;
      case FormatType.Alphanumeric:
        return (
          actualPrefix.toLowerCase() === configuredPrefix.toLowerCase() &&
          /^[a-zA-Z0-9]*$/.test(actualPrefix)
        );
      case FormatType.AlphanumericWithSigns:
        return (
          actualPrefix.toLowerCase() === configuredPrefix.toLowerCase() &&
          [...actualPrefix].every((c) => c.charCodeAt(0) > 0x20 && c.charCodeAt(0) < 0x7f)
        );
      default:
        return false;
    }
  }

  private toDateInputValue(date?: string | null): string | null {
    if (!date) {
      return null;
    }

    return date.substring(0, 10);
  }
}
